import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Dropdown, Icon } from 'semantic-ui-react';
import moment from 'moment';
import { importAlertState } from 'src/redux/actions/alert';
import CustomModal from 'src/components/configuracion/components/customModal';
import EOAFCategoryDropdown from 'src/containers/EOAFCategoryDropdown';
import { GetContactEOAFAssignments, SaveContactEOAFAssignmentsBulk } from 'src/graphql/eoaf';
import { useEOAFContext } from 'src/context';
import 'src/components/configuracion2/sections/CategorizacionObligacionesSection.scss';
import './ContactEoafSection.scss';

const emptyRow = () => ({
  periodSince: '',
  creditSumOrRest: 'SUMA',
  creditCategoryKey: '',
  creditCategoryType: '',
  debitSumOrRest: 'RESTA',
  debitCategoryKey: '',
  debitCategoryType: '',
});

function findLabelInTree(tree, key) {
  if (!Array.isArray(tree) || !key) return null;
  for (const node of tree) {
    if (node.key === key) return node.label;
    const found = findLabelInTree(node.children || [], key);
    if (found) return found;
  }
  return null;
}

export function ContactEoafSection({ companyId, contactId, contactName = '' }) {
  const dispatch = useDispatch();
  const eoafContext = useEOAFContext?.() ?? {};
  const structureTree = eoafContext?.structureTree ?? [];
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [classificationRows, setClassificationRows] = useState([emptyRow()]);
  const [existingRowsSnapshotOnOpen, setExistingRowsSnapshotOnOpen] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { data: assignmentsData, reexecuteQuery: refetchAssignments } = GetContactEOAFAssignments({
    companyId: companyId || '',
    contactId: contactId || '',
  });
  const assignmentsList = Array.isArray(assignmentsData) ? assignmentsData : [];
  const contactDoc = contactId ? assignmentsList.find((d) => d.contactId === contactId) : assignmentsList[0];
  const eoafArea = contactDoc?.eoafArea ?? [];
  const { executeMutation: executeSaveBulk } = SaveContactEOAFAssignmentsBulk();

  const periodOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 24; i++) {
      const period = moment().subtract(i, 'months').format('YYYYMM');
      const label = moment(period, 'YYYYMM').format('MMMM YYYY');
      options.push({
        key: period,
        value: period,
        text: label.charAt(0).toUpperCase() + label.slice(1),
      });
    }
    return options;
  }, []);

  /** Solo la entrada con periodo más cercano al actual (el historial completo se ve en el modal). */
  const displayEntry = useMemo(() => {
    if (!eoafArea?.length) return null;
    const currentNum = parseInt(moment().format('YYYYMM'), 10);
    const closest = eoafArea.reduce((best, e) => {
      const p = parseInt(e.periodSince || '0', 10) || 0;
      const dist = Math.abs(p - currentNum);
      if (!best) return e;
      const bestDist = Math.abs(parseInt(best.periodSince || '0', 10) - currentNum);
      if (dist < bestDist) return e;
      if (dist === bestDist && p > parseInt(best.periodSince || '0', 10)) return e;
      return best;
    }, null);
    return closest;
  }, [eoafArea]);

  const openAssignModal = () => {
    if (eoafArea?.length) {
      const sorted = [...eoafArea].sort((a, b) => (a.periodSince || '').localeCompare(b.periodSince || ''));
      const rows = sorted.map((e) => ({
        periodSince: e.periodSince || '',
        creditSumOrRest: e.creditSumOrRest || 'SUMA',
        creditCategoryKey: e.creditCategoryKey || '',
        creditCategoryType: e.creditCategoryType || '',
        debitSumOrRest: e.debitSumOrRest || 'RESTA',
        debitCategoryKey: e.debitCategoryKey || '',
        debitCategoryType: e.debitCategoryType || '',
      }));
      setClassificationRows(rows);
      setExistingRowsSnapshotOnOpen(rows.map((r) => ({ ...r })));
    } else {
      setClassificationRows([emptyRow()]);
      setExistingRowsSnapshotOnOpen([]);
    }
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
  };

  const setRow = (index, patch) => {
    setClassificationRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const handleAddClassificationRow = () => {
    const last = classificationRows[classificationRows.length - 1];
    setClassificationRows((prev) => [
      ...prev,
      {
        ...emptyRow(),
        creditCategoryKey: last?.creditCategoryKey || '',
        creditCategoryType: last?.creditCategoryType || '',
        debitCategoryKey: last?.debitCategoryKey || '',
        debitCategoryType: last?.debitCategoryType || '',
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    if (classificationRows.length <= 1) return;
    setClassificationRows((prev) => prev.filter((_, i) => i !== index));
  };

  const performSave = async () => {
    if (!companyId || !contactId) return;
    const entries = classificationRows.map((r) => ({
      periodSince: r.periodSince?.trim() || undefined,
      creditCategoryKey: r.creditCategoryKey?.trim() || undefined,
      creditCategoryType: r.creditCategoryType?.trim() || undefined,
      creditSumOrRest: 'SUMA',
      debitCategoryKey: r.debitCategoryKey?.trim() || undefined,
      debitCategoryType: r.debitCategoryType?.trim() || undefined,
      debitSumOrRest: 'RESTA',
    }));
    const result = await executeSaveBulk({
      input: { companyId, contactId, entries },
    });
    if (result.error) {
      dispatch(
        importAlertState({
          content: result.error.message || 'Error al guardar la categorización.',
          state: 'error',
          duration: 4,
        })
      );
      return;
    }
    dispatch(
      importAlertState({
        content: 'Categorización guardada correctamente.',
        state: 'success',
        duration: 3,
      })
    );
    refetchAssignments({ requestPolicy: 'network-only' });
    setConfirmModalOpen(false);
    closeAssignModal();
  };

  const handleGuardar = async () => {
    const hasAnyCategory = classificationRows.some(
      (r) =>
        (r.creditCategoryKey && r.creditCategoryKey.trim()) ||
        (r.debitCategoryKey && r.debitCategoryKey.trim())
    );
    if (!hasAnyCategory) {
      dispatch(
        importAlertState({
          content: 'Selecciona al menos una categoría (Abono o Cargo) en alguna fila para guardar.',
          state: 'warning',
          duration: 4,
        })
      );
      return;
    }
    if (!companyId || !contactId) {
      closeAssignModal();
      return;
    }
    const isRowModified = (current, initial) => {
      if (!initial || current.periodSince?.trim() !== initial.periodSince?.trim()) return false;
      return (
        (current.creditCategoryKey || '').trim() !== (initial.creditCategoryKey || '').trim() ||
        (current.creditCategoryType || '').trim() !== (initial.creditCategoryType || '').trim() ||
        (current.creditSumOrRest || '') !== (initial.creditSumOrRest || '') ||
        (current.debitCategoryKey || '').trim() !== (initial.debitCategoryKey || '').trim() ||
        (current.debitCategoryType || '').trim() !== (initial.debitCategoryType || '').trim() ||
        (current.debitSumOrRest || '') !== (initial.debitSumOrRest || '')
      );
    };
    const modifiedExistingPeriod = classificationRows.some((r) => {
      const period = r.periodSince?.trim();
      if (!period) return false;
      const initial = existingRowsSnapshotOnOpen.find((s) => (s.periodSince || '').trim() === period);
      return isRowModified(r, initial);
    });
    if (modifiedExistingPeriod) {
      setConfirmModalOpen(true);
      return;
    }
    await performSave();
  };

  const handleConfirmModify = () => {
    performSave();
  };

  const periodLabel = displayEntry?.periodSince
    ? moment(displayEntry.periodSince, 'YYYYMM').format('MMMM YYYY').replace(/^./, (c) => c.toUpperCase())
    : null;
  const creditLabel = displayEntry?.creditCategoryKey
    ? findLabelInTree(structureTree, displayEntry.creditCategoryKey) || displayEntry.creditCategoryKey
    : null;
  const debitLabel = displayEntry?.debitCategoryKey
    ? findLabelInTree(structureTree, displayEntry.debitCategoryKey) || displayEntry.debitCategoryKey
    : null;

  return (
    <div className="contact-eoaf-section parametrizacion-section">
      <div className="parametrization-divider" style={{ marginTop: 24 }} />
      <div className="contact-eoaf-field">
        <label className="label">CATEGORÍA EOAF</label>
        {displayEntry && (creditLabel || debitLabel || periodLabel) ? (
          <div className="contact-eoaf-box contact-eoaf-box--row">
            <span className="contact-eoaf-box-categories">
              {creditLabel && (
                <span className="contact-eoaf-box-categories-part">
                  <span className="contact-eoaf-tag">{(creditLabel || '—').trim()} (A)</span>
                  {debitLabel && <span className="contact-eoaf-separator">, </span>}
                </span>
              )}
              {debitLabel && <span className="contact-eoaf-tag">{(debitLabel || '—').trim()} (C)</span>}
              {!creditLabel && !debitLabel && <span className="contact-eoaf-tag contact-eoaf-tag--muted">—</span>}
            </span>
            {periodLabel && <span className="contact-eoaf-box-desde">Desde: {periodLabel}</span>}
          </div>
        ) : (
          <div className="contact-eoaf-box contact-eoaf-box--empty">
            No hay categorías EOAF asignadas a este contacto.
          </div>
        )}
      </div>
      <div className="contact-eoaf-actions">
        <button
          type="button"
          className="parametrization-btn parametrization-btn--primary contact-eoaf-assign-btn"
          onClick={openAssignModal}
        >
          Asignar Categoría
        </button>
      </div>

      <CustomModal
        open={assignModalOpen}
        onClose={closeAssignModal}
        title={contactName ? `Asignar Categoría EOAF - ${contactName}` : 'Asignar Categoría EOAF'}
        classNameModal="asignar-eoaf-modal"
        classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
        size="small"
        dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
        modalStyle={{
          borderRadius: '16px',
          maxWidth: '95%',
          minWidth: '1080px',
          margin: '0 auto',
        }}
        content={
          <div className="asignar-eoaf-modal-content">
            <div className="asignar-eoaf-rows-container">
              <table className="asignar-eoaf-table">
                <thead>
                  <tr>
                    <th>Desde</th>
                    <th>Abono</th>
                    <th>Cargo</th>
                    <th style={{ width: 44, textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {classificationRows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <Dropdown
                          selection
                          placeholder="Periodo"
                          options={[{ key: '', value: '', text: '—' }, ...periodOptions]}
                          value={row.periodSince}
                          onChange={(e, { value }) => setRow(index, { periodSince: value || '' })}
                          className="asignar-eoaf-dropdown"
                        />
                      </td>
                      <td>
                        <EOAFCategoryDropdown
                          companyId={companyId}
                          value={row.creditCategoryKey}
                          onChange={(e, { value, node }) =>
                            setRow(index, {
                              creditCategoryKey: value || '',
                              creditCategoryType: node?.type ?? '',
                            })
                          }
                          placeholder="Categoría"
                          className="asignar-eoaf-dropdown"
                        />
                      </td>
                      <td>
                        <EOAFCategoryDropdown
                          companyId={companyId}
                          value={row.debitCategoryKey}
                          onChange={(e, { value, node }) =>
                            setRow(index, {
                              debitCategoryKey: value || '',
                              debitCategoryType: node?.type ?? '',
                            })
                          }
                          placeholder="Categoría"
                          className="asignar-eoaf-dropdown"
                        />
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {classificationRows.length > 1 && (
                          <button
                            type="button"
                            className="asignar-eoaf-row-remove-x"
                            onClick={() => handleRemoveRow(index)}
                            title="Eliminar fila"
                            aria-label="Eliminar fila"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="asignar-eoaf-add-button"
                onClick={handleAddClassificationRow}
              >
                <Icon name="plus" />
                Agregar categoría
              </button>
            </div>
          </div>
        }
        customActions={
          <div className="tw-flex tw-justify-end tw-items-center">
            <Button onClick={closeAssignModal}>Cancelar</Button>
            <Button color="blue" onClick={handleGuardar}>
              Guardar
            </Button>
          </div>
        }
      />

      <CustomModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="Confirmar modificación"
        size="tiny"
        dimmer={{ style: { zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.5)' } }}
        content={
          <p style={{ margin: 0 }}>
            Está a punto de modificar la categorización para uno o más periodos que ya existían. ¿Desea continuar?
          </p>
        }
        customActions={
          <div className="tw-flex tw-justify-end tw-items-center tw-gap-2">
            <Button onClick={() => setConfirmModalOpen(false)}>Cancelar</Button>
            <Button color="blue" onClick={handleConfirmModify}>
              Confirmar
            </Button>
          </div>
        }
      />
    </div>
  );
}
